import type { ConfiguratorDefinition } from "~/features/configurator/common/v2/StepDefinition";
import { SubstepType } from "~/features/configurator/Linear/dtos";
import { RESET_SELECTION, RESET_SELECTION_AND_LENGTH } from "~/features/configurator/common/advancedTranslationUtils";
import { getCsticsValue } from "~/features/configurator/common/v2/helper";
import * as MetricShaftCstics from "~/features/configurator/Shaft/metric/metricCsticConstants";
import {
  DIAMETER,
  SERIES,
  SHAFT_MACHINING_LEFT,
  SHAFT_MACHINING_MIDDLE,
  SHAFT_MACHINING_RIGHT,
} from "~/features/configurator/Shaft/metric/metricCsticConstants";
import { userIsCustomer } from "~/domains/user/utils/roles";
import {
  isChamferManualWidth,
  showAxialThreadSection,
  showChamferedSection,
  showRadialThreadSection,
} from "~/features/configurator/Shaft/shaftConfiguratorHelper";

export const MetricShaftConfiguratorDefinition: ConfiguratorDefinition = {
  description: [
    {
      condition: ({ mainKmat }) => {
        const [series] = getCsticsValue(mainKmat, SERIES);
        return series === "W";
      },
      value: "configurator.shaft.step.1.substep.1.tile.1.description.metric",
    },
    {
      condition: ({ mainKmat }) => {
        const [series] = getCsticsValue(mainKmat, SERIES);
        return series === "WH";
      },
      value: "configurator.shaft.step.1.substep.1.tile.2.description",
    },
  ],
  mediaUrl: [
    {
      condition: ({ mainKmat }) => {
        const [series] = getCsticsValue(mainKmat, SERIES);
        return series === "WH";
      },
      value: "configurator.shaft.productImage.WH.noHoles",
    },
    {
      condition: ({ mainKmat }) => {
        const [shaftMiddle, shaftLeft, shaftRight] = getCsticsValue(
          mainKmat,
          SHAFT_MACHINING_MIDDLE,
          SHAFT_MACHINING_LEFT,
          SHAFT_MACHINING_RIGHT,
        );
        return shaftMiddle === "980" && (["003", "007"].includes(shaftLeft) || ["003", "007"].includes(shaftRight));
      },
      value: "configurator.shaft.productImage.W.axialRadialThread",
    },
    {
      condition: ({ mainKmat }) => {
        const [shaftMiddle, shaftLeft, shaftRight] = getCsticsValue(
          mainKmat,
          SHAFT_MACHINING_MIDDLE,
          SHAFT_MACHINING_LEFT,
          SHAFT_MACHINING_RIGHT,
        );
        return shaftMiddle === "900" && (["003", "007"].includes(shaftLeft) || ["003", "007"].includes(shaftRight));
      },
      value: "configurator.shaft.productImage.W.axialThread",
    },
    {
      condition: ({ mainKmat }) => {
        const [shaftMiddle] = getCsticsValue(mainKmat, SHAFT_MACHINING_MIDDLE);
        return shaftMiddle === "980";
      },
      value: "configurator.shaft.productImage.W.radialThread",
    },
    {
      condition: ({ mainKmat }) => {
        const [shaftMiddle] = getCsticsValue(mainKmat, SHAFT_MACHINING_MIDDLE);
        return shaftMiddle === "900";
      },
      value: "configurator.shaft.productImage.W.noHoles",
    },
  ],
  pages: [
    {
      name: "configurator.shaft.step.1.title",
      enabled: true,
      resetLabel: RESET_SELECTION,
      resetRequiresConfirmation: true,
      substeps: [
        {
          type: SubstepType.Series,

          name: "configurator.shaft.step.1.substep.1.title",
          description: "configurator.shaft.step.1.substep.1.description",

          tiles: [
            {
              name: "configurator.shaft.step.1.substep.1.tile.1.title.metric",
              description: "configurator.shaft.step.1.substep.1.tile.1.description.metric",
              imageUrl: "configurator.shaft.step.1.substep.1.tile.1.image.metric",
              info: "configurator.shaft.step.1.substep.1.tile.1.infoPopup.metric",
              cstics: [
                {
                  csticName: "VMKB_060",
                  value: "W",
                },
              ],
            },
            {
              name: "configurator.shaft.step.1.substep.1.tile.2.title",
              description: "configurator.shaft.step.1.substep.1.tile.2.description",
              imageUrl: "configurator.shaft.step.1.substep.1.tile.2.image",
              info: "configurator.shaft.step.1.substep.1.tile.2.infoPopup",
              cstics: [
                {
                  csticName: "VMKB_060",
                  value: "WH",
                },
              ],
            },
          ],
          visible: true,

          tilesPerRow: 6,
          csticsToReset: ["VMKB_060"],
        },
        {
          type: SubstepType.Diameter,

          name: "configurator.shaft.step.1.substep.2.title.metric",
          description: "configurator.shaft.step.1.substep.2.description",

          tiles: [
            {
              name: "configurator.shaft.step.1.substep.2.tile.1.title.metric",
              cstics: [
                {
                  csticName: "VBGR_060",
                  value: "4.0",
                },
              ],
              disabled: [
                {
                  condition: ({ mainKmat }) => {
                    const [series] = getCsticsValue(mainKmat, SERIES);
                    return !series;
                  },
                  value: true,
                },
                {
                  condition: ({ mainKmat }) => true,
                  value: false,
                },
              ],
            },
            {
              name: "configurator.shaft.step.1.substep.2.tile.2.title.metric",
              cstics: [
                {
                  csticName: "VBGR_060",
                  value: "5.0",
                },
              ],
              disabled: [
                {
                  condition: ({ mainKmat }) => {
                    const [series] = getCsticsValue(mainKmat, SERIES);
                    return !series;
                  },
                  value: true,
                },
                {
                  condition: ({ mainKmat }) => true,
                  value: false,
                },
              ],
            },
            {
              name: "configurator.shaft.step.1.substep.2.tile.3.title.metric",
              cstics: [
                {
                  csticName: "VBGR_060",
                  value: "6.0",
                },
              ],
              disabled: [
                {
                  condition: ({ mainKmat }) => {
                    const [series] = getCsticsValue(mainKmat, SERIES);
                    return !series;
                  },
                  value: true,
                },
                {
                  condition: ({ mainKmat }) => true,
                  value: false,
                },
              ],
            },
            {
              name: "configurator.shaft.step.1.substep.2.tile.4.title.metric",
              cstics: [
                {
                  csticName: "VBGR_060",
                  value: "8.0",
                },
              ],
              disabled: [
                {
                  condition: ({ mainKmat }) => {
                    const [series] = getCsticsValue(mainKmat, SERIES);
                    return !series;
                  },
                  value: true,
                },
                {
                  condition: ({ mainKmat }) => true,
                  value: false,
                },
              ],
            },
            {
              name: "configurator.shaft.step.1.substep.2.tile.5.title.metric",
              cstics: [
                {
                  csticName: "VBGR_060",
                  value: "10.0",
                },
              ],
              disabled: [
                {
                  condition: ({ mainKmat }) => {
                    const [series] = getCsticsValue(mainKmat, SERIES);
                    return !series;
                  },
                  value: true,
                },
                {
                  condition: ({ mainKmat }) => true,
                  value: false,
                },
              ],
            },
            {
              name: "configurator.shaft.step.1.substep.2.tile.6.title.metric",
              cstics: [
                {
                  csticName: "VBGR_060",
                  value: "12.0",
                },
              ],
              disabled: [
                {
                  condition: ({ mainKmat }) => {
                    const [series] = getCsticsValue(mainKmat, SERIES);
                    return !series;
                  },
                  value: true,
                },
                {
                  condition: ({ mainKmat }) => true,
                  value: false,
                },
              ],
            },
            {
              name: "configurator.shaft.step.1.substep.2.tile.7.title.metric",
              cstics: [
                {
                  csticName: "VBGR_060",
                  value: "14.0",
                },
              ],
              disabled: [
                {
                  condition: ({ mainKmat }) => {
                    const [series] = getCsticsValue(mainKmat, SERIES);
                    return !series;
                  },
                  value: true,
                },
                {
                  condition: ({ mainKmat }) => true,
                  value: false,
                },
              ],
            },
            {
              name: "configurator.shaft.step.1.substep.2.tile.8.title.metric",
              cstics: [
                {
                  csticName: "VBGR_060",
                  value: "15.0",
                },
              ],
              disabled: [
                {
                  condition: ({ mainKmat }) => {
                    const [series] = getCsticsValue(mainKmat, SERIES);
                    return !series;
                  },
                  value: true,
                },
                {
                  condition: ({ mainKmat }) => true,
                  value: false,
                },
              ],
            },
            {
              name: "configurator.shaft.step.1.substep.2.tile.9.title.metric",
              cstics: [
                {
                  csticName: "VBGR_060",
                  value: "16.0",
                },
              ],
              disabled: [
                {
                  condition: ({ mainKmat }) => {
                    const [series] = getCsticsValue(mainKmat, SERIES);
                    return !series;
                  },
                  value: true,
                },
                {
                  condition: ({ mainKmat }) => true,
                  value: false,
                },
              ],
            },
            {
              name: "configurator.shaft.step.1.substep.2.tile.10.title.metric",
              cstics: [
                {
                  csticName: "VBGR_060",
                  value: "20.0",
                },
              ],
              disabled: [
                {
                  condition: ({ mainKmat }) => {
                    const [series] = getCsticsValue(mainKmat, SERIES);
                    return !series;
                  },
                  value: true,
                },
                {
                  condition: ({ mainKmat }) => true,
                  value: false,
                },
              ],
            },
            {
              name: "configurator.shaft.step.1.substep.2.tile.11.title.metric",
              cstics: [
                {
                  csticName: "VBGR_060",
                  value: "25.0",
                },
              ],
              disabled: [
                {
                  condition: ({ mainKmat }) => {
                    const [series] = getCsticsValue(mainKmat, SERIES);
                    return !series;
                  },
                  value: true,
                },
                {
                  condition: ({ mainKmat }) => true,
                  value: false,
                },
              ],
            },
            {
              name: "configurator.shaft.step.1.substep.2.tile.12.title.metric",
              cstics: [
                {
                  csticName: "VBGR_060",
                  value: "30.0",
                },
              ],
              disabled: [
                {
                  condition: ({ mainKmat }) => {
                    const [series] = getCsticsValue(mainKmat, SERIES);
                    return !series;
                  },
                  value: true,
                },
                {
                  condition: ({ mainKmat }) => true,
                  value: false,
                },
              ],
            },
            {
              name: "configurator.shaft.step.1.substep.2.tile.13.title.metric",
              cstics: [
                {
                  csticName: "VBGR_060",
                  value: "40.0",
                },
              ],
              disabled: [
                {
                  condition: ({ mainKmat }) => {
                    const [series] = getCsticsValue(mainKmat, SERIES);
                    return !series;
                  },
                  value: true,
                },
                {
                  condition: ({ mainKmat }) => true,
                  value: false,
                },
              ],
            },
            {
              name: "configurator.shaft.step.1.substep.2.tile.14.title.metric",
              cstics: [
                {
                  csticName: "VBGR_060",
                  value: "50.0",
                },
              ],
              disabled: [
                {
                  condition: ({ mainKmat }) => {
                    const [series] = getCsticsValue(mainKmat, SERIES);
                    return !series;
                  },
                  value: true,
                },
                {
                  condition: ({ mainKmat }) => true,
                  value: false,
                },
              ],
            },
            {
              name: "configurator.shaft.step.1.substep.2.tile.15.title.metric",
              cstics: [
                {
                  csticName: "VBGR_060",
                  value: "60.0",
                },
              ],
              disabled: [
                {
                  condition: ({ mainKmat }) => {
                    const [series] = getCsticsValue(mainKmat, SERIES);
                    return !series;
                  },
                  value: true,
                },
                {
                  condition: ({ mainKmat }) => true,
                  value: false,
                },
              ],
            },
            {
              name: "configurator.shaft.step.1.substep.2.tile.16.title.metric",
              cstics: [
                {
                  csticName: "VBGR_060",
                  value: "80.0",
                },
              ],
              disabled: [
                {
                  condition: ({ mainKmat }) => {
                    const [series] = getCsticsValue(mainKmat, SERIES);
                    return !series;
                  },
                  value: true,
                },
                {
                  condition: ({ mainKmat }) => true,
                  value: false,
                },
              ],
            },
          ],
          visible: true,

          footerInfo: [
            [
              {
                condition: ({ mainKmat }) => {
                  const [series, diameter] = getCsticsValue(mainKmat, SERIES, DIAMETER);
                  return series === "WH" && diameter === "12.0";
                },
                value: "configurator.shaft.step.1.substep.2.additionalInfo.6",
              },
              {
                condition: ({ mainKmat }) => {
                  const [series, diameter] = getCsticsValue(mainKmat, SERIES, DIAMETER);
                  return series === "WH" && diameter === "16.0";
                },
                value: "configurator.shaft.step.1.substep.2.additionalInfo.9",
              },
              {
                condition: ({ mainKmat }) => {
                  const [series, diameter] = getCsticsValue(mainKmat, SERIES, DIAMETER);
                  return series === "WH" && diameter === "20.0";
                },
                value: "configurator.shaft.step.1.substep.2.additionalInfo.10",
              },
              {
                condition: ({ mainKmat }) => {
                  const [series, diameter] = getCsticsValue(mainKmat, SERIES, DIAMETER);
                  return series === "WH" && diameter === "25.0";
                },
                value: "configurator.shaft.step.1.substep.2.additionalInfo.11",
              },
              {
                condition: ({ mainKmat }) => {
                  const [series, diameter] = getCsticsValue(mainKmat, SERIES, DIAMETER);
                  return series === "WH" && diameter === "30.0";
                },
                value: "configurator.shaft.step.1.substep.2.additionalInfo.12",
              },
              {
                condition: ({ mainKmat }) => {
                  const [series, diameter] = getCsticsValue(mainKmat, SERIES, DIAMETER);
                  return series === "WH" && diameter === "40.0";
                },
                value: "configurator.shaft.step.1.substep.2.additionalInfo.13",
              },
              {
                condition: ({ mainKmat }) => {
                  const [series, diameter] = getCsticsValue(mainKmat, SERIES, DIAMETER);
                  return series === "WH" && diameter === "50.0";
                },
                value: "configurator.shaft.step.1.substep.2.additionalInfo.14",
              },
              {
                condition: ({ mainKmat }) => {
                  const [series, diameter] = getCsticsValue(mainKmat, SERIES, DIAMETER);
                  return series === "WH" && diameter === "60.0";
                },
                value: "configurator.shaft.step.1.substep.2.additionalInfo.15",
              },
              {
                condition: ({ mainKmat }) => {
                  const [series, diameter] = getCsticsValue(mainKmat, SERIES, DIAMETER);
                  return series === "WH" && diameter === "80.0";
                },
                value: "configurator.shaft.step.1.substep.2.additionalInfo.16",
              },
            ],
          ],

          tilesPerRow: 8,
          csticsToReset: ["VBGR_060"],
        },
        {
          type: SubstepType.Material,

          name: "configurator.shaft.step.1.substep.3.title",
          description: "configurator.shaft.step.1.substep.3.description",

          tiles: [
            {
              name: "configurator.shaft.step.1.substep.3.tile.1.title",
              info: "configurator.shaft.step.1.substep.3.tile.1.infoPopup",
              cstics: [
                [
                  {
                    condition: ({ mainKmat }) => {
                      const [series] = getCsticsValue(mainKmat, SERIES);
                      return series === "WH";
                    },
                    value: {
                      csticName: "VWST_060",
                      value: "C60",
                    },
                  },
                  {
                    condition: ({ mainKmat }) => true,
                    value: {
                      csticName: "VWST_060",
                      value: "CF53",
                    },
                  },
                ],
              ],
              disabled: [
                {
                  condition: ({ mainKmat }) => {
                    const [series] = getCsticsValue(mainKmat, SERIES);
                    return !series;
                  },
                  value: true,
                },
                {
                  condition: ({ mainKmat }) => true,
                  value: false,
                },
              ],
            },
            {
              name: "configurator.shaft.step.1.substep.3.tile.2.title",
              info: "configurator.shaft.step.1.substep.3.tile.2.infoPopup",
              cstics: [
                {
                  csticName: "VWST_060",
                  value: "X46",
                },
              ],
              disabled: [
                {
                  condition: ({ mainKmat }) => {
                    const [series] = getCsticsValue(mainKmat, SERIES);
                    return !series;
                  },
                  value: true,
                },
                {
                  condition: ({ mainKmat }) => true,
                  value: false,
                },
              ],
            },
            {
              name: "configurator.shaft.step.1.substep.3.tile.3.title",
              info: "configurator.shaft.step.1.substep.3.tile.3.infoPopup",
              cstics: [
                {
                  csticName: "VWST_060",
                  value: "X90",
                },
              ],
              disabled: [
                {
                  condition: ({ mainKmat }) => {
                    const [series] = getCsticsValue(mainKmat, SERIES);
                    return !series;
                  },
                  value: true,
                },
                {
                  condition: ({ mainKmat }) => true,
                  value: false,
                },
              ],
            },
          ],
          visible: true,

          tilesPerRow: 4,
          csticsToReset: ["VWST_060"],
        },
        {
          type: SubstepType.Coating,

          name: "configurator.shaft.step.1.substep.4.title",
          description: "configurator.shaft.step.1.substep.4.description",
          info: "configurator.shaft.step.1.substep.4.infoPopup",

          tiles: [
            {
              name: "configurator.shaft.step.1.substep.4.tile.1.title",
              cstics: [
                {
                  csticName: "VBSCH060",
                  value: "/",
                },
              ],
              disabled: [
                {
                  condition: ({ mainKmat }) => {
                    const [series] = getCsticsValue(mainKmat, SERIES);
                    return !series;
                  },
                  value: true,
                },
                {
                  condition: ({ mainKmat }) => true,
                  value: false,
                },
              ],
            },
            {
              name: "configurator.shaft.step.1.substep.4.tile.2.title",
              description: "configurator.shaft.step.1.substep.4.tile.2.description",
              info: "configurator.shaft.step.1.substep.4.tile.2.infoPopup",
              cstics: [
                {
                  csticName: "VBSCH060",
                  value: "CR",
                },
              ],
              disabled: [
                {
                  condition: ({ mainKmat }) => {
                    const [series] = getCsticsValue(mainKmat, SERIES);
                    return !series;
                  },
                  value: true,
                },
                {
                  condition: ({ mainKmat }) => true,
                  value: false,
                },
              ],
            },
            {
              name: "configurator.shaft.step.1.substep.4.tile.3.title",
              description: "configurator.shaft.step.1.substep.4.tile.3.description",
              info: "configurator.shaft.step.1.substep.4.tile.3.infoPopup",
              cstics: [
                {
                  csticName: "VBSCH060",
                  value: "RROC",
                },
              ],
              disabled: [
                {
                  condition: ({ mainKmat }) => {
                    const [series] = getCsticsValue(mainKmat, SERIES);
                    return !series;
                  },
                  value: true,
                },
                {
                  condition: ({ mainKmat }) => true,
                  value: false,
                },
              ],
            },
          ],
          visible: true,

          tilesPerRow: 4,
          csticsToReset: ["VBSCH060"],
        },
        {
          type: SubstepType.Tolerance,

          name: "configurator.shaft.step.1.substep.5.title",
          description: "configurator.shaft.step.1.substep.5.description",
          info: "configurator.shaft.step.1.substep.5.infoPopup.metric",

          tiles: [
            {
              name: "configurator.shaft.step.1.substep.5.tile.1.title.metric",
              cstics: [
                {
                  csticName: "VTOL_060",
                  value: "H6",
                },
              ],
              disabled: [
                {
                  condition: ({ mainKmat }) => {
                    const [series] = getCsticsValue(mainKmat, SERIES);
                    return !series;
                  },
                  value: true,
                },
                {
                  condition: ({ mainKmat }) => true,
                  value: false,
                },
              ],
            },
            {
              name: "configurator.shaft.step.1.substep.5.tile.2.title.metric",
              cstics: [
                {
                  csticName: "VTOL_060",
                  value: "H7",
                },
              ],
              disabled: [
                {
                  condition: ({ mainKmat }) => {
                    const [series] = getCsticsValue(mainKmat, SERIES);
                    return !series;
                  },
                  value: true,
                },
                {
                  condition: ({ mainKmat }) => true,
                  value: false,
                },
              ],
            },
          ],
          visible: true,

          tilesPerRow: 6,
          csticsToReset: ["VTOL_060"],
        },
      ],
    },
    {
      name: "configurator.shaft.step.2.title",
      enabled: true,
      resetLabel: RESET_SELECTION_AND_LENGTH,
      resetRequiresConfirmation: true,
      substeps: [
        {
          type: SubstepType.ShaftLength,
          name: "configurator.shaft.step.2.substep.1.title.metric",

          tiles: [],

          inputCstics: {
            ["lengthMetric"]: {
              name: MetricShaftCstics.SHAFT_LENGTH,
              required: true,
            },
          },

          csticsToReset: [MetricShaftCstics.SHAFT_LENGTH],
          visible: true,
        },
        {
          type: SubstepType.ShaftTolerance,
          name: "configurator.shaft.step.2.substep.2.title.metric",

          tiles: [
            {
              name: "configurator.shaft.step.2.substep.2.tile.1.title",

              cstics: [
                {
                  csticName: "VLMAX_01A_060",
                  value: "LN",
                },
              ],
            },
            {
              name: "configurator.shaft.step.2.substep.2.tile.2.title",

              cstics: [
                {
                  csticName: "VLMAX_01A_060",
                  value: "LS",
                },
              ],
            },
            // {
            //   name: "configurator.shaft.step.2.substep.2.tile.3.title",
            //
            //   cstics: [
            //     {
            //       csticName: "VLMAX_01A_060",
            //       value: "LNS",
            //     },
            //   ],
            // },
          ],

          tilesPerRow: 3,
          csticsToReset: ["VLMAX_01A_060"],
          visible: true,
        },
        {
          type: SubstepType.ShaftMachining,
          name: "configurator.shaft.step.2.substep.3.title",

          groupedTiles: [
            {
              name: "configurator.shaft.step.2.substep.3.title",
              tiles: [
                {
                  name: "configurator.shaft.step.2.substep.3.tile.000.title",
                  description: "configurator.shaft.step.2.substep.3.tile.000.description",
                  info: "configurator.shaft.step.2.substep.3.tile.000.infoPopup.metric.left",
                  imageUrl: "configurator.shaft.step.2.substep.3.tile.000.image.left",
                  cstics: [
                    {
                      csticName: MetricShaftCstics.SHAFT_MACHINING_LEFT,
                      value: "000",
                    },
                  ],
                },
                {
                  name: "configurator.shaft.step.2.substep.3.tile.001.title",
                  description: "configurator.shaft.step.2.substep.3.tile.001.description",
                  info: "configurator.shaft.step.2.substep.3.tile.001.infoPopup.left.metric",
                  imageUrl: "configurator.shaft.step.2.substep.3.tile.001.image.left",
                  cstics: [
                    {
                      csticName: MetricShaftCstics.SHAFT_MACHINING_LEFT,
                      value: "001",
                    },
                  ],
                },
                {
                  name: "configurator.shaft.step.2.substep.3.tile.005.title",
                  description: "configurator.shaft.step.2.substep.3.tile.005.description",
                  info: "configurator.shaft.step.2.substep.3.tile.005.infoPopup.left.metric",
                  imageUrl: "configurator.shaft.step.2.substep.3.tile.005.image.left",
                  cstics: [
                    {
                      csticName: MetricShaftCstics.SHAFT_MACHINING_LEFT,
                      value: "005",
                    },
                  ],
                },
                {
                  name: "configurator.shaft.step.2.substep.3.tile.003.title",
                  description: "configurator.shaft.step.2.substep.3.tile.003.description",
                  info: "configurator.shaft.step.2.substep.3.tile.003.infoPopup.left",
                  imageUrl: "configurator.shaft.step.2.substep.3.tile.003.image.left",
                  cstics: [
                    {
                      csticName: MetricShaftCstics.SHAFT_MACHINING_LEFT,
                      value: "003",
                    },
                  ],
                },
                {
                  name: "configurator.shaft.step.2.substep.3.tile.007.title",
                  description: "configurator.shaft.step.2.substep.3.tile.007.description",
                  info: "configurator.shaft.step.2.substep.3.tile.007.infoPopup.left",
                  imageUrl: "configurator.shaft.step.2.substep.3.tile.007.image.left",
                  cstics: [
                    {
                      csticName: MetricShaftCstics.SHAFT_MACHINING_LEFT,
                      value: "007",
                    },
                  ],
                },
              ],
              visible: true,
            },
            {
              name: "configurator.shaft.step.2.substep.3.title",
              tiles: [
                {
                  name: "configurator.shaft.step.2.substep.3.tile.900.title",
                  description: "configurator.shaft.step.2.substep.3.tile.900.description",
                  imageUrl: "configurator.shaft.step.2.substep.3.tile.900.image",
                  cstics: [
                    {
                      csticName: MetricShaftCstics.SHAFT_MACHINING_MIDDLE,
                      value: "900",
                    },
                  ],
                },
                {
                  name: "configurator.shaft.step.2.substep.3.tile.980.title.metric",
                  description: "configurator.shaft.step.2.substep.3.tile.980.description.metric",
                  info: "configurator.shaft.step.2.substep.3.tile.980.infoPopup.metric",
                  imageUrl: "configurator.shaft.step.2.substep.3.tile.980.image",
                  cstics: [
                    {
                      csticName: MetricShaftCstics.SHAFT_MACHINING_MIDDLE,
                      value: "980",
                    },
                  ],
                },
              ],
              visible: true,
            },
            {
              name: "configurator.shaft.step.2.substep.3.title",
              tiles: [
                {
                  name: "configurator.shaft.step.2.substep.3.tile.000.title",
                  description: "configurator.shaft.step.2.substep.3.tile.000.description",
                  info: "configurator.shaft.step.2.substep.3.tile.000.infoPopup.metric.right",
                  imageUrl: "configurator.shaft.step.2.substep.3.tile.000.image.right",
                  cstics: [
                    {
                      csticName: MetricShaftCstics.SHAFT_MACHINING_RIGHT,
                      value: "000",
                    },
                  ],
                },
                {
                  name: "configurator.shaft.step.2.substep.3.tile.001.title",
                  description: "configurator.shaft.step.2.substep.3.tile.001.description",
                  info: "configurator.shaft.step.2.substep.3.tile.001.infoPopup.right.metric",
                  imageUrl: "configurator.shaft.step.2.substep.3.tile.001.image.right",
                  cstics: [
                    {
                      csticName: MetricShaftCstics.SHAFT_MACHINING_RIGHT,
                      value: "001",
                    },
                  ],
                },
                {
                  name: "configurator.shaft.step.2.substep.3.tile.005.title",
                  description: "configurator.shaft.step.2.substep.3.tile.005.description",
                  info: "configurator.shaft.step.2.substep.3.tile.005.infoPopup.right.metric",
                  imageUrl: "configurator.shaft.step.2.substep.3.tile.005.image.right",
                  cstics: [
                    {
                      csticName: MetricShaftCstics.SHAFT_MACHINING_RIGHT,
                      value: "005",
                    },
                  ],
                },
                {
                  name: "configurator.shaft.step.2.substep.3.tile.003.title",
                  description: "configurator.shaft.step.2.substep.3.tile.003.description",
                  info: "configurator.shaft.step.2.substep.3.tile.003.infoPopup.right",
                  imageUrl: "configurator.shaft.step.2.substep.3.tile.003.image.right",
                  cstics: [
                    {
                      csticName: MetricShaftCstics.SHAFT_MACHINING_RIGHT,
                      value: "003",
                    },
                  ],
                },
                {
                  name: "configurator.shaft.step.2.substep.3.tile.007.title",
                  description: "configurator.shaft.step.2.substep.3.tile.007.description",
                  info: "configurator.shaft.step.2.substep.3.tile.007.infoPopup.right",
                  imageUrl: "configurator.shaft.step.2.substep.3.tile.007.image.right",
                  cstics: [
                    {
                      csticName: MetricShaftCstics.SHAFT_MACHINING_RIGHT,
                      value: "007",
                    },
                  ],
                },
              ],
              visible: true,
            },
          ],

          inputCstics: {
            ["machiningLeft"]: {
              name: MetricShaftCstics.SHAFT_MACHINING_LEFT,
              required: true,
            },
            ["machiningMiddle"]: {
              name: MetricShaftCstics.SHAFT_MACHINING_MIDDLE,
              required: true,
            },

            ["machiningRight"]: {
              name: MetricShaftCstics.SHAFT_MACHINING_RIGHT,
              required: true,
            },
          },

          tilesPerRow: 3,
          csticsToReset: [
            MetricShaftCstics.SHAFT_MACHINING_LEFT,
            MetricShaftCstics.SHAFT_MACHINING_MIDDLE,
            MetricShaftCstics.SHAFT_MACHINING_RIGHT,
          ],
          visible: true,
        },
      ],
    },
    {
      name: "configurator.shaft.step.3.title",
      enabled: true,
      resetLabel: RESET_SELECTION,
      resetRequiresConfirmation: true,
      substeps: [
        {
          type: SubstepType.ShaftMachiningExtra,
          visible: true,

          inputCstics: {
            chamferWidthLeftManual: {
              name: MetricShaftCstics.chamfer.left.width.sf,
              required: [
                {
                  condition: ({ mainKmat }) => {
                    const [machiningLeft, chamferAngle] = getCsticsValue(
                      mainKmat,
                      MetricShaftCstics.SHAFT_MACHINING_LEFT,
                      MetricShaftCstics.chamfer.left.angle,
                    );
                    return showChamferedSection(machiningLeft) && isChamferManualWidth(chamferAngle);
                  },
                  value: true,
                },
                false,
              ],
            },
            "axialThread.left.size": {
              name: MetricShaftCstics.axialThread.left.size,
              required: [
                {
                  condition: ({ mainKmat }) => {
                    const [machiningLeft] = getCsticsValue(mainKmat, MetricShaftCstics.SHAFT_MACHINING_LEFT);
                    return showAxialThreadSection(machiningLeft);
                  },
                  value: true,
                },
                false,
              ],
            },

            // Middle
            "radialThread.size": {
              name: MetricShaftCstics.radialThread.size,
              required: [
                {
                  condition: ({ mainKmat }) => {
                    const [machiningMiddle] = getCsticsValue(mainKmat, MetricShaftCstics.SHAFT_MACHINING_MIDDLE);
                    return showRadialThreadSection(machiningMiddle);
                  },
                  value: true,
                },
                false,
              ],
            },
            "radialThread.aL": {
              name: MetricShaftCstics.radialThread.aL.value,
              required: [
                {
                  condition: ({ mainKmat }) => {
                    const [machiningMiddle] = getCsticsValue(mainKmat, MetricShaftCstics.SHAFT_MACHINING_MIDDLE);
                    return showRadialThreadSection(machiningMiddle);
                  },
                  value: true,
                },
                false,
              ],
            },
            "radialThread.spacing.recommended": {
              name: MetricShaftCstics.radialThread.threadSpacing.recommended,
              required: [
                {
                  condition: ({ mainKmat }) => {
                    const [machiningMiddle, threadSpacingMode] = getCsticsValue(
                      mainKmat,
                      MetricShaftCstics.SHAFT_MACHINING_MIDDLE,
                      MetricShaftCstics.radialThread.threadSpacing.mode,
                    );
                    return (
                      showRadialThreadSection(machiningMiddle) &&
                      threadSpacingMode === MetricShaftCstics.radialThread.threadSpacing.modeRecommended
                    );
                  },
                  value: true,
                },
                false,
              ],
            },
            "radialThread.spacing.individual": {
              name: MetricShaftCstics.radialThread.threadSpacing.individual.value,
              required: [
                {
                  condition: ({ mainKmat }) => {
                    const [machiningMiddle, threadSpacingMode] = getCsticsValue(
                      mainKmat,
                      MetricShaftCstics.SHAFT_MACHINING_MIDDLE,
                      MetricShaftCstics.radialThread.threadSpacing.mode,
                    );
                    return (
                      showRadialThreadSection(machiningMiddle) &&
                      threadSpacingMode === MetricShaftCstics.radialThread.threadSpacing.modeIndividual
                    );
                  },
                  value: true,
                },
                false,
              ],
            },

            // Right

            chamferWidthRightManual: {
              name: MetricShaftCstics.chamfer.right.width.sf,
              required: [
                {
                  condition: ({ mainKmat }) => {
                    const [machiningRight, chamferAngle] = getCsticsValue(
                      mainKmat,
                      MetricShaftCstics.SHAFT_MACHINING_RIGHT,
                      MetricShaftCstics.chamfer.right.angle,
                    );
                    return showChamferedSection(machiningRight) && isChamferManualWidth(chamferAngle);
                  },
                  value: true,
                },
                false,
              ],
            },

            "axialThread.right.size": {
              name: MetricShaftCstics.axialThread.right.size,
              required: [
                {
                  condition: ({ mainKmat }) => {
                    const [machiningRight] = getCsticsValue(mainKmat, MetricShaftCstics.SHAFT_MACHINING_RIGHT);
                    return showAxialThreadSection(machiningRight);
                  },
                  value: true,
                },
                false,
              ],
            },
          },
        },
      ],
    },
    {
      name: "configurator.shaft.step.4.title",
      enabled: true,
      resetLabel: "",
      resetRequiresConfirmation: false,
      substeps: [
        {
          type: SubstepType.SummaryPage,
          visible: true,

          info: [
            {
              condition: () => {
                return userIsCustomer();
              },
              value: "configurator.shaft.summary.message.designation.customer",
            },
            {
              condition: () => {
                return !userIsCustomer();
              },
              value: "configurator.shaft.summary.message.designation.public",
            },
          ],
        },
      ],
    },
  ],
};

// TODO - Fill out summary data for both inch and metric (image, description)
